import React, { useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Divider,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  InputAdornment
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import Swal from 'sweetalert2';
import { GET_URL, POST_URL } from 'Includes/urls';
import { getRequest, postRequest } from 'Includes/api/apicall';
import _ from 'lodash';

function CopyOrdersToNextYearModal(modalProps) {
  const {
    open,
    onClose,
    loading,
    initialFormData,
    availableYears,
    onSuccess,
    props: routerProps
  } = modalProps
  
  const props = routerProps || modalProps

  const [targetYear, setTargetYear] = React.useState('');
  const [formData, setFormData] = React.useState({});
  const [ordersDataForYear, setordersDataForYear] = React.useState({});
  const [comapnies, setCompanies] = React.useState([]);
  const [serviceErrors, setServiceErrors] = React.useState({}); // { serviceIndex: { fieldName: errorMessage } }
  const [termErrors, setTermErrors] = React.useState({}); // { serviceIndex: { termIndex_field: errorMessage } }
  const [serviceValidationErrors, setServiceValidationErrors] = React.useState({}); // { serviceIndex: errorMessage }
  const [submitting, setSubmitting] = React.useState(false);
  const [submitErrorMessage, setSubmitErrorMessage] = React.useState('');
  const hasValidatedOnOpen = useRef(false);

  // Helper function to add 1 year to a date while keeping month and date the same
  const addOneYearToDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      date.setFullYear(date.getFullYear() + 1);
      // Format as YYYY-MM-DD
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (e) {
      return '';
    }
  };

  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const companiesSelected = useRef({})
  React.useEffect(() => {
    if (open && initialFormData) {
      // Set default dates before setting formData
      const dataWithDefaults = _.cloneDeep(initialFormData);
      if (dataWithDefaults.pricing_services) {
        dataWithDefaults.pricing_services.forEach((service) => {
          // Set order_date to today's date
          if (!service.order_date) {
            service.order_date = getTodayDate();
          }
          
          // Set service_end_date: add 1 year to original date
          if (!service.service_end_date && service.original_service_end_date) {
            service.service_end_date = addOneYearToDate(service.original_service_end_date);
          }
          
          // Set term_receive_date: add 1 year to original date for each term
          if (service.payment_terms) {
            service.payment_terms.forEach((term) => {
              if (!term.term_receive_date && term.original_term_receive_date) {
                term.term_receive_date = addOneYearToDate(term.original_term_receive_date);
              }
            });
          }
        });
      }
      
      setFormData(dataWithDefaults);
      companiesSelected.current = {}
      initialFormData.pricing_services.map((data)=>{
        companiesSelected.current[data?.company] = data?.company_name 
      })
      setSubmitting(false);
      setTargetYear('');
      setServiceErrors({});
      setTermErrors({});
      setServiceValidationErrors({});
      hasValidatedOnOpen.current = false;
    } else if (!open) {
      hasValidatedOnOpen.current = false;
    }
  }, [open, initialFormData]);

  // Validate data when formData is set after modal opens
  React.useEffect(() => {
    if (open && formData && formData.pricing_services && formData.pricing_services.length > 0 && !hasValidatedOnOpen.current) {
      // Validate all data when modal opens and formData is ready (only once)
      hasValidatedOnOpen.current = true;
      setTimeout(() => {
        validateAllServicesOnOpen(formData);
      }, 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, formData]);

  const handleClose = () => {
    setTargetYear('');
    setFormData({});
    setSubmitErrorMessage('');
    onClose();
  };

  const getCompanies = (year) => {
    const url = GET_URL.company_billing_config.api
    const list_services_opted_for_companies = formData.pricing_services.map(d=>d.company) 
    getRequest(url, { year, list_services_opted_for_companies: list_services_opted_for_companies.toString() }, props)
      .then(response => {
        if (response && response.status === 200) {
          const data = response.data.data
          setordersDataForYear(response.data.orders)
          const companiesListed = {}
          data.forEach(d=>{
            companiesListed[d.company] = d
          })
          setCompanies(companiesListed)
          let errorMessage = ''
          for (const d of Object.keys(companiesSelected.current)) {
            if (!Object.keys(companiesListed).includes(d)) {
              errorMessage += `${companiesSelected.current[d]}, `
            }
          }
          if (errorMessage) {
            handleClose()
            Swal.fire({
              icon: 'error',
              title: 'Error',
              text: `Config Not set for this year: ${errorMessage}`,
            })
          } else {
            const ps = _.cloneDeep(formData)
            ps.pricing_services.forEach((data)=>{
              if (data?.serviceplan_data?.service_type === 'STUDENT_WISE') {
                data.item_count = companiesListed[data.company].student_strength
              }
            })
            setFormData(ps)
          }
        }
      })
      .catch(() => { })
  }

  const onChangeSelectedYear = (value) => {
    setTargetYear(value)
    setSubmitErrorMessage('') // Clear error when year changes
    getCompanies(value)
  }

  const addPaymentTerm = (serviceIndex) => {
    setFormData(prev => {
      const newData = _.cloneDeep(prev);
      if (!newData.pricing_services[serviceIndex].payment_terms) {
        newData.pricing_services[serviceIndex].payment_terms = [];
      }
      const termNumber = newData.pricing_services[serviceIndex].payment_terms.length + 1;
      newData.pricing_services[serviceIndex].payment_terms.push({
        term_name: `Term ${termNumber}`,
        term_receive_date: '',
        amount_percentage: '',
        amount_rs: ''
      });
      return newData;
    });
    
    // Clear errors for this service's terms
    setTermErrors(prev => {
      const newErrors = { ...prev };
      if (newErrors[serviceIndex]) {
        delete newErrors[serviceIndex];
      }
      return newErrors;
    });
    setServiceValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[serviceIndex];
      return newErrors;
    });
  };

  const removePaymentTerm = (serviceIndex, termIndex) => {
    setFormData(prev => {
      const newData = _.cloneDeep(prev);
      if (newData.pricing_services[serviceIndex].payment_terms && 
          newData.pricing_services[serviceIndex].payment_terms.length > 1) {
        newData.pricing_services[serviceIndex].payment_terms.splice(termIndex, 1);
      }
      return newData;
    });
    
    // Clear errors for removed term
    setTermErrors(prev => {
      const newErrors = { ...prev };
      if (newErrors[serviceIndex]) {
        const updatedErrors = { ...newErrors[serviceIndex] };
        // Remove all errors for this term index
        Object.keys(updatedErrors).forEach(key => {
          if (key.startsWith(`${termIndex}_`)) {
            delete updatedErrors[key];
          }
        });
        if (Object.keys(updatedErrors).length === 0) {
          delete newErrors[serviceIndex];
        } else {
          newErrors[serviceIndex] = updatedErrors;
        }
      }
      return newErrors;
    });
    
    // Re-validate service terms after removal
    setTimeout(() => {
      validateServiceTerms(serviceIndex);
    }, 100);
  };

  const handleFormFieldChange = (field, value, serviceIndex = null, termIndex = null) => {
    // Clear submit error message when user makes changes
    if (submitErrorMessage) {
      setSubmitErrorMessage('');
    }

    if (serviceIndex !== null) {
      // Update service field
      setFormData(prev => {
        const newData = { ...prev };
        if (termIndex !== null) {
          // Update payment term field
          newData.pricing_services[serviceIndex].payment_terms[termIndex] = {
            ...newData.pricing_services[serviceIndex].payment_terms[termIndex],
            [field]: value
          };
        } else {
          // Update service field
          newData.pricing_services[serviceIndex] = {
            ...newData.pricing_services[serviceIndex],
            [field]: value
          };
        }
        return newData;
      });
      
      // Clear error for this field when it's updated
      if (termIndex !== null) {
        setTermErrors(prev => {
          const newErrors = { ...prev };
          if (newErrors[serviceIndex]) {
            newErrors[serviceIndex] = { ...newErrors[serviceIndex] };
            delete newErrors[serviceIndex][`${termIndex}_${field}`];
            if (Object.keys(newErrors[serviceIndex]).length === 0) {
              delete newErrors[serviceIndex];
            }
          }
          return newErrors;
        });
      } else {
        setServiceErrors(prev => {
          const newErrors = { ...prev };
          if (newErrors[serviceIndex]) {
            newErrors[serviceIndex] = { ...newErrors[serviceIndex] };
            delete newErrors[serviceIndex][field];
            if (Object.keys(newErrors[serviceIndex]).length === 0) {
              delete newErrors[serviceIndex];
            }
          }
          return newErrors;
        });
      }
    } else {
      // Update top-level field
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const validateServiceField = (field, value, serviceIndex) => {
    const service = formData.pricing_services?.[serviceIndex];
    if (!service) return;

    let error = '';
    
    switch (field) {
      case 'item_count':
        const itemCount = parseFloat(value);
        if (!value || isNaN(itemCount) || itemCount <= 0) {
          error = 'Item Count is required and must be greater than 0';
        }
        break;
      case 'service_amount':
        const amount = parseFloat(value);
        if (!value || isNaN(amount) || amount <= 0) {
          error = 'Service Amount is required and must be greater than 0';
        } else if (amount > 1000000) {
          error = 'Service Amount must not exceed 1,000,000';
        }
        break;
      case 'service_gst_percentage':
        if (value === '' || value === null || value === undefined) {
          error = 'GST % is required';
        } else {
          const gst = parseFloat(value);
          if (isNaN(gst) || gst < 0 || gst > 100) {
            error = 'GST % must be between 0 and 100';
          }
        }
        break;
      case 'order_date':
        if (!value) {
          error = 'Order Date is required';
        }
        break;
      case 'service_end_date':
        if (!value) {
          error = 'Service End Date is required';
        }
        break;
      case 'service_duration':
        if (value === '' || value === null || value === undefined) {
          error = 'Service Duration is required';
        } else {
          const duration = parseFloat(value);
          if (isNaN(duration) || duration < 0) {
            error = 'Service Duration must be 0 or greater';
          } else if (duration > 100) {
            error = 'Service Duration must not exceed 100';
          }
        }
        break;
      default:
        break;
    }

    setServiceErrors(prev => {
      const newErrors = { ...prev };
      if (!newErrors[serviceIndex]) {
        newErrors[serviceIndex] = {};
      }
      if (error) {
        newErrors[serviceIndex][field] = error;
      } else {
        delete newErrors[serviceIndex][field];
        if (Object.keys(newErrors[serviceIndex]).length === 0) {
          delete newErrors[serviceIndex];
        }
      }
      return newErrors;
    });
  };

  const validateTermField = (field, value, serviceIndex, termIndex) => {
    const service = formData.pricing_services?.[serviceIndex];
    const term = service?.payment_terms?.[termIndex];
    if (!term) return;

    let error = '';

    switch (field) {
      case 'term_name':
        if (!value || value.trim() === '') {
          error = 'Term Name is required';
        }
        break;
      case 'term_receive_date':
        if (!value) {
          error = 'Term Receive Date is required';
        }
        break;
      case 'amount_percentage':
      case 'amount_rs':
        // Get the current term value after the change
        const currentTerm = { ...term, [field]: value };
        // If one field is being set, we'll clear the other in the form data update
        // But for validation, check the current state
        if (!currentTerm.amount_percentage && !currentTerm.amount_rs) {
          error = 'Either Amount % or Amount (Rs) is required';
        } else if (field === 'amount_rs' && value) {
          // Validate max value for amount_rs
          const amountRs = parseFloat(value);
          if (!isNaN(amountRs) && amountRs > 1000000) {
            error = 'Amount (Rs) must not exceed 1,000,000';
          }
        }
        // Update the form data to clear the other field if this one has a value
        if (value && field === 'amount_percentage') {
          setFormData(prev => {
            const newData = _.cloneDeep(prev);
            if (newData.pricing_services[serviceIndex]?.payment_terms[termIndex]) {
              newData.pricing_services[serviceIndex].payment_terms[termIndex].amount_rs = '';
            }
            // Validate with updated data
            setTimeout(() => validateServiceTerms(serviceIndex, newData), 0);
            return newData;
          });
          // Clear error for amount_rs
          setTermErrors(prev => {
            const newErrors = { ...prev };
            if (newErrors[serviceIndex]) {
              newErrors[serviceIndex] = { ...newErrors[serviceIndex] };
              delete newErrors[serviceIndex][`${termIndex}_amount_rs`];
              delete newErrors[serviceIndex][`${termIndex}_amount`];
            }
            return newErrors;
          });
        } else if (value && field === 'amount_rs') {
          setFormData(prev => {
            const newData = _.cloneDeep(prev);
            if (newData.pricing_services[serviceIndex]?.payment_terms[termIndex]) {
              newData.pricing_services[serviceIndex].payment_terms[termIndex].amount_percentage = '';
            }
            // Validate with updated data
            setTimeout(() => validateServiceTerms(serviceIndex, newData), 0);
            return newData;
          });
          // Clear error for amount_percentage
          setTermErrors(prev => {
            const newErrors = { ...prev };
            if (newErrors[serviceIndex]) {
              newErrors[serviceIndex] = { ...newErrors[serviceIndex] };
              delete newErrors[serviceIndex][`${termIndex}_amount_percentage`];
              delete newErrors[serviceIndex][`${termIndex}_amount`];
            }
            return newErrors;
          });
        }
        break;
      case 'term_name':
        // Optional field
        break;
      default:
        break;
    }

    setTermErrors(prev => {
      const newErrors = { ...prev };
      if (!newErrors[serviceIndex]) {
        newErrors[serviceIndex] = {};
      }
      if (error) {
        newErrors[serviceIndex][`${termIndex}_${field}`] = error;
        // Also set generic amount error if it's an amount field
        if (field === 'amount_percentage' || field === 'amount_rs') {
          newErrors[serviceIndex][`${termIndex}_amount`] = error;
        }
      } else {
        delete newErrors[serviceIndex][`${termIndex}_${field}`];
        if (field === 'amount_percentage' || field === 'amount_rs') {
          delete newErrors[serviceIndex][`${termIndex}_amount`];
        }
        if (Object.keys(newErrors[serviceIndex]).length === 0) {
          delete newErrors[serviceIndex];
        }
      }
      return newErrors;
    });

    // Validate payment terms for the service after field change
    // Use setTimeout to ensure formData is updated
    if (field !== 'amount_percentage' && field !== 'amount_rs') {
      setTimeout(() => {
        validateServiceTerms(serviceIndex);
      }, 100);
    }
  };

  const validateServiceTerms = (serviceIndex, updatedFormData = null) => {
    const dataToUse = updatedFormData || formData;
    const service = dataToUse.pricing_services?.[serviceIndex];
    if (!service || !service.payment_terms || service.payment_terms.length === 0) {
      setServiceValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[serviceIndex];
        return newErrors;
      });
      return;
    }

    const terms = service.payment_terms;
    let validationError = '';

    // Check if all terms have required fields
    const hasMissingFields = terms.some(term => !term.term_name || !term.term_name.trim() || !term.term_receive_date || (!term.amount_percentage && !term.amount_rs));
    if (hasMissingFields) {
      // Individual field errors are handled by validateTermField
      setServiceValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[serviceIndex];
        return newErrors;
      });
      return;
    }

    // Validate that term dates are in ascending order
    if (terms.length > 1) {
      for (let i = 0; i < terms.length - 1; i++) {
        const currentDate = new Date(terms[i].term_receive_date);
        const nextDate = new Date(terms[i + 1].term_receive_date);
        if (currentDate >= nextDate) {
          validationError = `Term ${i + 1} date must be earlier than Term ${i + 2} date`;
          break;
        }
      }
    }

    // Check if all terms use the same type (% or Rs)
    const termsWithPercentage = terms.filter(t => t.amount_percentage && parseFloat(t.amount_percentage) > 0);
    const termsWithRs = terms.filter(t => t.amount_rs && parseFloat(t.amount_rs) > 0);

    if (termsWithPercentage.length > 0 && termsWithRs.length > 0) {
      validationError = 'All terms must use the same payment type. Please use either all "Amount in %" or all "Amount in Rs"';
    } else if (termsWithPercentage.length > 0) {
      // Validate sum of percentages equals 100%
      const sumPercentage = terms.reduce((sum, term) => {
        return sum + (parseFloat(term.amount_percentage) || 0);
      }, 0);
      if (Math.abs(sumPercentage - 100) > 0.01) {
        validationError = `Sum of all term percentages must equal 100%. Current sum: ${sumPercentage.toFixed(2)}%`;
      }
    } else if (termsWithRs.length > 0) {
      // Calculate base amount (service_amount * item_count for ITEM_WISE, or service_amount for others)
      const serviceAmount = parseFloat(service.service_amount) || 0;
      const itemCount = parseFloat(service.item_count) || 1;
      const serviceType = service?.serviceplan_data?.service_type;
      let baseAmount = serviceAmount;
      
      if (serviceType === 'ITEM_WISE') {
        baseAmount = serviceAmount * itemCount;
      } else if (serviceType === 'STUDENT_WISE') {
        const studentStrength = comapnies[service.company]?.student_strength || service?.billing_config_data?.student_strength || itemCount;
        baseAmount = serviceAmount * studentStrength;
      }

      const sumRs = terms.reduce((sum, term) => {
        return sum + (parseFloat(term.amount_rs) || 0);
      }, 0);
      
      if (Math.abs(sumRs - baseAmount) > 0.01) {
        validationError = `Sum of all term amounts must equal Base Amount (₹${baseAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}). Current sum: ₹${sumRs.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
    }

    setServiceValidationErrors(prev => {
      const newErrors = { ...prev };
      if (validationError) {
        newErrors[serviceIndex] = validationError;
      } else {
        delete newErrors[serviceIndex];
      }
      return newErrors;
    });
  };

  const hasServiceErrors = (serviceIndex) => {
    const hasFieldErrors = serviceErrors[serviceIndex] && Object.keys(serviceErrors[serviceIndex]).length > 0;
    const hasTermErrors = termErrors[serviceIndex] && Object.keys(termErrors[serviceIndex]).length > 0;
    const hasValidationError = !!serviceValidationErrors[serviceIndex];
    return hasFieldErrors || hasTermErrors || hasValidationError;
  };

  const hasAnyErrors = () => {
    if (!targetYear) return true;
    if (Object.keys(serviceErrors).length > 0) return true;
    if (Object.keys(termErrors).length > 0) return true;
    if (Object.keys(serviceValidationErrors).length > 0) return true;
    return false;
  };

  const getServicePricingSummary = (service, serviceIndex) => {
    const serviceAmount = parseFloat(service.service_amount || 0) || 0;
    const gstPercent = parseFloat(service.service_gst_percentage || 0) || 0;
    const itemCount = parseFloat(service.item_count || 1) || 1;
    const serviceType = service?.serviceplan_data?.service_type;
    
    // Get student strength
    const studentStrength = comapnies[service.company]?.student_strength || service?.billing_config_data?.student_strength || itemCount;
    
    let baseAmount = 0;
    if (serviceType === 'STUDENT_WISE') {
      baseAmount = serviceAmount * studentStrength;
    } else if (serviceType === 'ITEM_WISE') {
      baseAmount = serviceAmount * itemCount;
    } else {
      baseAmount = serviceAmount;
    }
    
    const gstAmount = gstPercent > 0 ? (baseAmount * (gstPercent / 100)) : 0;
    const totalAmount = baseAmount + gstAmount;
    
    return { baseAmount, gstAmount, totalAmount, gstPercent };
  };

  const formatAmount = (v) => {
    const n = parseFloat(v || 0);
    return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const validateAllServicesOnOpen = (dataToValidate) => {
    // Validate all service fields using the provided data
    const data = dataToValidate || formData;
    if (!data || !data.pricing_services) return;
    
    // Use setTimeout to ensure formData state is updated before validation
    setTimeout(() => {
      data.pricing_services.forEach((service, serviceIndex) => {
        // Validate service fields - these functions read from formData state
        // So we need to ensure formData is set first
        validateServiceField('item_count', service.item_count, serviceIndex);
        validateServiceField('service_amount', service.service_amount, serviceIndex);
        validateServiceField('service_gst_percentage', service.service_gst_percentage, serviceIndex);
        validateServiceField('order_date', service.order_date, serviceIndex);
        validateServiceField('service_end_date', service.service_end_date, serviceIndex);
        validateServiceField('service_duration', service.service_duration, serviceIndex);
        
        // Validate all payment terms
        service.payment_terms?.forEach((term, termIndex) => {
          validateTermField('term_name', term.term_name, serviceIndex, termIndex);
          validateTermField('term_receive_date', term.term_receive_date, serviceIndex, termIndex);
          if (term.amount_percentage) {
            validateTermField('amount_percentage', term.amount_percentage, serviceIndex, termIndex);
          } else if (term.amount_rs) {
            validateTermField('amount_rs', term.amount_rs, serviceIndex, termIndex);
          } else {
            validateTermField('amount_percentage', '', serviceIndex, termIndex);
          }
        });
      });
      
      // Validate service terms after a delay to ensure all field validations are done
      setTimeout(() => {
        data.pricing_services.forEach((_, serviceIndex) => {
          validateServiceTerms(serviceIndex);
        });
      }, 300);
    }, 200);
  };

  const validateAllServices = () => {
    // Validate all service fields
    formData.pricing_services?.forEach((service, serviceIndex) => {
      validateServiceField('item_count', service.item_count, serviceIndex);
      validateServiceField('service_amount', service.service_amount, serviceIndex);
      validateServiceField('service_gst_percentage', service.service_gst_percentage, serviceIndex);
      validateServiceField('order_date', service.order_date, serviceIndex);
      validateServiceField('service_end_date', service.service_end_date, serviceIndex);
      validateServiceField('service_duration', service.service_duration, serviceIndex);
      
      // Validate all payment terms
      service.payment_terms?.forEach((term, termIndex) => {
        validateTermField('term_name', term.term_name, serviceIndex, termIndex);
        validateTermField('term_receive_date', term.term_receive_date, serviceIndex, termIndex);
        if (term.amount_percentage) {
          validateTermField('amount_percentage', term.amount_percentage, serviceIndex, termIndex);
        } else if (term.amount_rs) {
          validateTermField('amount_rs', term.amount_rs, serviceIndex, termIndex);
        } else {
          validateTermField('amount_percentage', '', serviceIndex, termIndex);
        }
      });
      
      // Validate service terms
      validateServiceTerms(serviceIndex);
    });
  };

  const handleSubmit = async () => {
    // Clear previous error message
    setSubmitErrorMessage('');

    if (!targetYear) {
      setSubmitErrorMessage('Please select target academic year');
      return;
    }

    if (!formData.pricing_services || formData.pricing_services.length === 0) {
      setSubmitErrorMessage('No services to copy');
      return;
    }

    // Validate all fields before submitting
    validateAllServices();

    // Wait a bit for validation to complete
    await new Promise(resolve => setTimeout(resolve, 100));

    // Additional max value validation before submit
    for (let serviceIndex = 0; serviceIndex < formData.pricing_services.length; serviceIndex++) {
      const service = formData.pricing_services[serviceIndex];
      
      // Validate service_amount max value
      const serviceAmount = parseFloat(service.service_amount);
      if (!isNaN(serviceAmount) && serviceAmount > 1000000) {
        setSubmitErrorMessage(`Service Amount must not exceed 1,000,000 for service: ${service.service_name || 'Service ' + (serviceIndex + 1)}`);
        return;
      }
      
      // Validate service_duration max value
      if (service.service_duration) {
        const serviceDuration = parseFloat(service.service_duration);
        if (!isNaN(serviceDuration) && serviceDuration > 100) {
          setSubmitErrorMessage(`Service Duration must not exceed 100 for service: ${service.service_name || 'Service ' + (serviceIndex + 1)}`);
          return;
        }
      }
      
      // Validate payment terms amount_rs max value
      if (service.payment_terms) {
        for (let termIndex = 0; termIndex < service.payment_terms.length; termIndex++) {
          const term = service.payment_terms[termIndex];
          if (term.amount_rs) {
            const amountRs = parseFloat(term.amount_rs);
            if (!isNaN(amountRs) && amountRs > 1000000) {
              setSubmitErrorMessage(`Amount (Rs) must not exceed 1,000,000 for ${term.term_name || 'Term ' + (termIndex + 1)} in service: ${service.service_name || 'Service ' + (serviceIndex + 1)}`);
              return;
            }
          }
        }
      }
    }

    // Check if there are any errors after validation
    if (hasAnyErrors()) {
      setSubmitErrorMessage('Please fix all validation errors before submitting');
      return;
    }

    // Check each service for errors
    const servicesWithErrors = formData.pricing_services?.some((_, index) => hasServiceErrors(index));
    if (servicesWithErrors) {
      setSubmitErrorMessage('Please fix all validation errors before submitting');
      return;
    }

    setSubmitting(true);

    const postClientData = {}
    for (const clientEachSvcData of formData.pricing_services) {
      const billing_config_for_selected_year = comapnies[clientEachSvcData.company].id
      if (!postClientData[billing_config_for_selected_year]) {
        postClientData[billing_config_for_selected_year] = {
          config_id: billing_config_for_selected_year,
          pricing_services: [],
          deletable_service_ids: [],
          deletable_service_term_ids: []
        }
      }
      postClientData[billing_config_for_selected_year].pricing_services.push({
        service_id: clientEachSvcData.service_id,
        service_name: clientEachSvcData.service_name,
        plan_id: clientEachSvcData.service_plan,
        plan_name: clientEachSvcData.plan_name,
        item_count: parseInt(clientEachSvcData.item_count || 1),
        service_amount: parseFloat(clientEachSvcData.service_amount || 0),
        service_gst_percentage: parseFloat(clientEachSvcData.service_gst_percentage || 0),
        payment_option: clientEachSvcData.payment_option || 'any',
        service_duration: clientEachSvcData.service_duration ? parseFloat(clientEachSvcData.service_duration) : null,
        order_date: clientEachSvcData.order_date, 
        service_end_date: clientEachSvcData.service_end_date || '',
        notes: clientEachSvcData.notes || '',
        payment_terms: clientEachSvcData.payment_terms.map(term => ({
          term_name: term.term_name || '',
          term_receive_date: term.term_receive_date || '',
          amount_percentage: term.amount_percentage ? parseFloat(term.amount_percentage) : null,
          amount_rs: term.amount_rs ? parseFloat(term.amount_rs) : null
        }))
      })
    }

      const response = await postRequest(POST_URL.billing_config.api, Object.values(postClientData), props);
    setSubmitting(false);
      
      if (response && response.status === 200) {
        Swal.fire({
          position: 'top-end',
          type: 'success',
          title: 'Success',
          text: 'Orders copied successfully',
          showConfirmButton: false,
          timer: 1500
        });
        handleClose();
        if (onSuccess) {
          onSuccess();
        }
      }
  };
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>Copy Orders to Next Year</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {/* Target Academic Year Selection */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined" required>
                  <InputLabel id="target-year-label">Select Target Academic Year</InputLabel>
                  <Select
                    labelId="target-year-label"
                    id="target-year-select"
                    value={targetYear}
                    onChange={(e) => onChangeSelectedYear(e.target.value)}
                    label="Select Target Academic Year"
                  >
                    {availableYears.map((year) => (
                      <MenuItem key={year.id} value={year.id}>
                        {year.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Show services only after target year is selected */}
            {targetYear && (
              <>
                <Divider sx={{ my: 2 }} />
                {formData.pricing_services?.map((service, serviceIndex) => (
              <Accordion key={serviceIndex} defaultExpanded={false} sx={{ mb: 2 }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`panel${serviceIndex}-content`}
                  id={`panel${serviceIndex}-header`}
                >
                  {(() => {
                    // Check if order already exists for this company and service_plan
                    const companyOrders = ordersDataForYear[service.company] || [];
                    const servicePlanId = service.service_plan;
                    // Handle both number and string comparisons
                    const orderExists = Array.isArray(companyOrders) && (
                      companyOrders.includes(servicePlanId) || 
                      companyOrders.includes(String(servicePlanId)) || 
                      companyOrders.includes(Number(servicePlanId))
                    );
                    
                    return (
                      <>
                        {orderExists && (
                          <Typography 
                            variant="subtitle1" 
                            sx={{ 
                              fontWeight: 'bold',
                              color: 'warning.main',
                              mr: 1
                            }}
                          >
                            Order Created Already - 
                          </Typography>
                        )}
                        <Typography 
                          variant="subtitle1" 
                          sx={{ 
                            fontWeight: 'bold',
                            color: hasServiceErrors(serviceIndex) ? 'error.main' : 'success.main'
                          }}
                        >
                          {service.service_name || ` Service ${serviceIndex + 1}`}
                          {service.company_name && ` for ${service.company_name}`} ({comapnies && comapnies[service.company] ? comapnies[service.company].student_strength : service?.billing_config_data?.student_strength} - Strength)
                        </Typography>
                      </>
                    );
                  })()}
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      required
                      label="Item Count "
                      type="number"
                      disabled={service?.serviceplan_data?.service_type !== 'ITEM_WISE'}
                      value={service.item_count || ''}
                      onChange={(e) => handleFormFieldChange('item_count', e.target.value, serviceIndex)}
                      onBlur={(e) => validateServiceField('item_count', e.target.value, serviceIndex)}
                      variant="outlined"
                      inputProps={{ min: 0 }}
                      error={!!(serviceErrors[serviceIndex]?.item_count)}
                      helperText={serviceErrors[serviceIndex]?.item_count || ''}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      required
                      label="Service Amount "
                      type="number"
                      value={service.service_amount || ''}
                      onChange={(e) => handleFormFieldChange('service_amount', e.target.value, serviceIndex)}
                      onBlur={(e) => validateServiceField('service_amount', e.target.value, serviceIndex)}
                      variant="outlined"
                      inputProps={{ min: 0, max: 1000000, step: 0.01 }}
                      error={!!(serviceErrors[serviceIndex]?.service_amount)}
                      helperText={serviceErrors[serviceIndex]?.service_amount || ''}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      required
                      label="GST %"
                      type="number"
                      value={service.service_gst_percentage ?? ''}
                      onChange={(e) => handleFormFieldChange('service_gst_percentage', e.target.value, serviceIndex)}
                      onBlur={(e) => validateServiceField('service_gst_percentage', e.target.value, serviceIndex)}
                      variant="outlined"
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                      error={!!(serviceErrors[serviceIndex]?.service_gst_percentage)}
                      helperText={serviceErrors[serviceIndex]?.service_gst_percentage || ''}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth variant="outlined" required>
                      <InputLabel>Payment Option</InputLabel>
                      <Select
                        value={service.payment_option || 'any'}
                        onChange={(e) => handleFormFieldChange('payment_option', e.target.value, serviceIndex)}
                        label="Payment Option"
                      >
                        <MenuItem value="with_gst_invoice_acc">With GST Invoice Acc</MenuItem>
                        <MenuItem value="without_gst_invoice_acc">Without GST Invoice Acc</MenuItem>
                        <MenuItem value="cash">Cash</MenuItem>
                        <MenuItem value="any">Any</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      required
                      label="Service Duration"
                      type="number"
                      value={service.service_duration ?? ''}
                      onChange={(e) => handleFormFieldChange('service_duration', e.target.value, serviceIndex)}
                      onBlur={(e) => validateServiceField('service_duration', e.target.value, serviceIndex)}
                      variant="outlined"
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">Years</InputAdornment>,
                      }}
                      error={!!(serviceErrors[serviceIndex]?.service_duration)}
                      helperText={serviceErrors[serviceIndex]?.service_duration || 'Measured in years'}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      required
                      label="Order Date"
                      type="date"
                      value={service.order_date || ''}
                      onChange={(e) => handleFormFieldChange('order_date', e.target.value, serviceIndex)}
                      onBlur={(e) => validateServiceField('order_date', e.target.value, serviceIndex)}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      error={!!(serviceErrors[serviceIndex]?.order_date)}
                      helperText={serviceErrors[serviceIndex]?.order_date || ''}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      required
                      label="Service End Date"
                      type="date"
                      value={service.service_end_date || ''}
                      onChange={(e) => handleFormFieldChange('service_end_date', e.target.value, serviceIndex)}
                      onBlur={(e) => validateServiceField('service_end_date', e.target.value, serviceIndex)}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      error={!!(serviceErrors[serviceIndex]?.service_end_date)}
                      helperText={serviceErrors[serviceIndex]?.service_end_date || ''}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Notes"
                      multiline
                      rows={2}
                      value={service.notes || ''}
                      onChange={(e) => handleFormFieldChange('notes', e.target.value, serviceIndex)}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>

                {/* Pricing Summary */}
                {(() => {
                  const { baseAmount, gstAmount, totalAmount, gstPercent } = getServicePricingSummary(service, serviceIndex);
                  return (
                    <Box mt={2} p={2} bgcolor="#f9f9f9" borderRadius={1} border={1} borderColor="grey.300">
                      <Typography variant="subtitle2" fontWeight="bold">Pricing Summary</Typography>
                      <Box display="flex" flexDirection="column" gap={0.5} mt={1}>
                        <Typography variant="caption" color="textSecondary">
                          Amount: {formatAmount(baseAmount)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          GST ({gstPercent || 0}%): {formatAmount(gstAmount)}
                        </Typography>
                        <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold' }}>
                          Total: {formatAmount(totalAmount)}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })()}

                {/* Payment Terms */}
                <Box sx={{ mt: 2, mb: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Payment Terms</Typography>
                    <IconButton 
                      color="primary" 
                      onClick={() => addPaymentTerm(serviceIndex)} 
                      size="small"
                      aria-label="Add payment term"
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                  {serviceValidationErrors[serviceIndex] && (
                    <Typography variant="caption" color="error" sx={{ mb: 1, display: 'block' }}>
                      {serviceValidationErrors[serviceIndex]}
                    </Typography>
                  )}
                  {!service.payment_terms || service.payment_terms.length === 0 ? (
                    <Box p={2} textAlign="center" bgcolor="#f5f5f5" borderRadius={1}>
                      <Typography variant="body2" color="textSecondary">
                        No payment terms added. Click + to add payment terms.
                      </Typography>
                    </Box>
                  ) : (
                    service.payment_terms.map((term, termIndex) => (
                      <Box key={termIndex} sx={{ mb: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                            Term {termIndex + 1}
                          </Typography>
                          {service.payment_terms.length > 1 && (
                            <IconButton
                              color="secondary"
                              onClick={() => removePaymentTerm(serviceIndex, termIndex)}
                              size="small"
                              aria-label="Remove payment term"
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={3}>
                            <TextField
                              fullWidth
                              required
                              label="Term Name"
                              value={term.term_name || ''}
                              onChange={(e) => handleFormFieldChange('term_name', e.target.value, serviceIndex, termIndex)}
                              onBlur={(e) => validateTermField('term_name', e.target.value, serviceIndex, termIndex)}
                              variant="outlined"
                              size="small"
                              error={!!(termErrors[serviceIndex]?.[`${termIndex}_term_name`])}
                              helperText={termErrors[serviceIndex]?.[`${termIndex}_term_name`] || ''}
                            />
                          </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          required
                          label="Term Receive Date"
                          type="date"
                          value={term.term_receive_date || ''}
                          onChange={(e) => handleFormFieldChange('term_receive_date', e.target.value, serviceIndex, termIndex)}
                          onBlur={(e) => validateTermField('term_receive_date', e.target.value, serviceIndex, termIndex)}
                          variant="outlined"
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          error={!!(termErrors[serviceIndex]?.[`${termIndex}_term_receive_date`])}
                          helperText={termErrors[serviceIndex]?.[`${termIndex}_term_receive_date`] || ''}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          required
                          label="Amount %"
                          type="number"
                          value={term.amount_percentage || ''}
                          onChange={(e) => handleFormFieldChange('amount_percentage', e.target.value, serviceIndex, termIndex)}
                          onBlur={(e) => validateTermField('amount_percentage', e.target.value, serviceIndex, termIndex)}
                          variant="outlined"
                          size="small"
                          inputProps={{ min: 0, max: 100, step: 0.01 }}
                          error={!!(termErrors[serviceIndex]?.[`${termIndex}_amount_percentage`] || termErrors[serviceIndex]?.[`${termIndex}_amount`])}
                          helperText={termErrors[serviceIndex]?.[`${termIndex}_amount_percentage`] || termErrors[serviceIndex]?.[`${termIndex}_amount`] || ''}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          required
                          label="Amount (Rs)"
                          type="number"
                          value={term.amount_rs || ''}
                          onChange={(e) => handleFormFieldChange('amount_rs', e.target.value, serviceIndex, termIndex)}
                          onBlur={(e) => validateTermField('amount_rs', e.target.value, serviceIndex, termIndex)}
                          variant="outlined"
                          size="small"
                          inputProps={{ min: 0, max: 1000000, step: 0.01 }}
                          error={!!(termErrors[serviceIndex]?.[`${termIndex}_amount_rs`] || termErrors[serviceIndex]?.[`${termIndex}_amount`])}
                          helperText={termErrors[serviceIndex]?.[`${termIndex}_amount_rs`] || termErrors[serviceIndex]?.[`${termIndex}_amount`] || ''}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                    ))
                  )}
                </Box>
                </AccordionDetails>
              </Accordion>
                ))}
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', pb: 2 }}>
        {submitErrorMessage && (
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'error.main', 
              fontWeight: 'bold',
              mb: 1,
              textAlign: 'center'
            }}
          >
            {submitErrorMessage}
          </Typography>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !targetYear || loading || hasAnyErrors()}
          >
            {submitting ? <CircularProgress size={20} /> : 'Submit'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default CopyOrdersToNextYearModal;

